import React from 'react';
import { IonContent, IonButton, IonSkeletonText } from '@ionic/react';
import SectionCard from '@/components/SectionCard';
import type { ActionGuardLock } from '@/components/ActionGuard';
import { pxToRem } from '@/modules/config/constants';

/**
 * Verification-axis copy for the step pages' share guard. The `[bracketed]` text
 * is the CTA label. The profile-attribution (extra-claim) case doesn't come from
 * here — its copy travels with the registered constraint's own descriptor.
 */
export const SHARE_STEP_HELP_TEXT = {
  singleVerification: 'Verify yourself to share this topic [Verify now]',
  updateProfile: 'Update your profile to share this topic [Jump to My Profile]',
  setupProfile: 'Set up your profile to share this topic [Jump to My Profile]',
};

// Used only if a lock's help text carries no [bracketed] label of its own; every
// string that reaches this page does.
const FALLBACK_BUTTON_LABEL = 'Continue';

/**
 * Fallback content for a share-flow step page when the user can't yet share.
 * Rendered as the `fallbackElem` of the page's `ActionGuard`, which hands over the
 * resolved `lock`: a skeleton of the compose layout plus the lock's own
 * explanation and CTA — whether the user owes attribution info (the
 * `profile_attribution` extra claim) or a verification. Since that line and the
 * button say everything the user needs, the page's `ActionGuardContainer` carries
 * no auto-toast.
 */
const ShareContentSkeleton: React.FC<{ lock: ActionGuardLock }> = ({
  lock,
}) => {
  return (
    <IonContent
      scrollY={false}
      style={{ '--background': 'var(--app-page-background)' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '4px 20px 0',
          boxSizing: 'border-box',
        }}
        data-testid="share-content-skeleton"
      >
        {/* Recipient-box strip skeleton */}
        <div style={{ marginBottom: '16px' }}>
          <IonSkeletonText
            animated
            style={{ width: '100%', height: '48px', borderRadius: '8px' }}
          />
        </div>
        {/* Message-preview card skeleton */}
        <SectionCard className="section-card--message-preview" noBottomMargin>
          <div style={{ padding: '4px 0' }}>
            <IonSkeletonText animated style={{ width: '80%' }} />
            <IonSkeletonText animated style={{ width: '95%' }} />
            <IonSkeletonText animated style={{ width: '60%' }} />
          </div>
        </SectionCard>
        <div style={{ flex: 1, minHeight: '16px' }} />
        <div style={{ padding: '16px 0 24px' }}>
          <p
            style={{
              fontSize: pxToRem(14),
              color: 'var(--ion-color-medium)',
              textAlign: 'center',
              margin: '0 0 12px',
            }}
            data-testid="share-content-skeleton-help-text"
          >
            {lock.helpText}
          </p>
          {lock.isUnlockable && (
            <IonButton
              expand="block"
              color="primary"
              onClick={lock.navigateToUnlock}
              data-testid="share-content-skeleton-unlock-button"
              style={{ margin: 0 }}
            >
              {lock.buttonText ?? FALLBACK_BUTTON_LABEL}
            </IonButton>
          )}
        </div>
      </div>
    </IonContent>
  );
};

export default ShareContentSkeleton;
